import React from 'react';
import { Link } from 'react-router-dom';
import { howItWorks, whyWorks, flowSteps, flowCards, workoutImage } from '../data/mock';
import { ArrowRight } from 'lucide-react';

export const HowItWorks = () => (
  <section className="bg-[#0b0b0c] py-14 lg:py-16">
    <div className="max-w-[1180px] mx-auto px-5 lg:px-8">
    <h2 className="text-white text-3xl lg:text-4xl font-black tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>How it works</h2>
      <div className="mt-6 grid md:grid-cols-3 gap-5">
        {howItWorks.map((s) => (
          <div key={s.n} className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-6 hover:border-[#BEFF38]/40 transition-colors">
            <div className="w-9 h-9 rounded-full bg-[#BEFF38]/10 border border-[#BEFF38]/40 text-[#BEFF38] font-black flex items-center justify-center text-sm">{s.n}</div>
            <h3 className="mt-4 text-white font-bold text-lg">{s.title}</h3>
            <p className="mt-2 text-[#aab1bf] text-[15px] leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const WhyWorks = () => (
  <section className="bg-[#0b0b0c] pb-14 lg:pb-16">
    <div className="max-w-[1180px] mx-auto px-5 lg:px-8">
      <h2 className="text-white text-3xl lg:text-4xl font-black tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Why this works</h2>
      <div className="mt-6 grid md:grid-cols-3 gap-5">
        {whyWorks.map((s) => (
          <div key={s.title} className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-6 hover:border-[#BEFF38]/40 transition-colors">
            <h3 className="text-white font-bold text-lg">{s.title}</h3>
            <p className="mt-2 text-[#aab1bf] text-[15px] leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>
      <div className="mt-7 rounded-2xl border border-[#BEFF38]/25 bg-[#BEFF38]/[0.04] p-6 lg:p-7">
        <p className="text-white font-bold">Your plan is the centre of everything.</p>
        <p className="mt-2 text-zinc-400 text-sm leading-relaxed">
          Workouts, nutrition, hydration and recovery all feed into the same personalised engine. The rest of the site supports the plan rather than competing with it.
        </p>
      </div>
    </div>
  </section>
);

export const Flow = () => (
  <section className="bg-[#0b0b0c] pb-14 lg:pb-16" id="my-plan">
    <div className="max-w-[1180px] mx-auto px-5 lg:px-8">
      <h2 className="text-white text-3xl lg:text-4xl font-black tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>The FreeFitFuel flow</h2>
      <div className="mt-6 grid md:grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-4">
        {flowSteps.map((s, idx) => (
          <React.Fragment key={s.title}>
            <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-6 text-center hover:border-[#BEFF38]/40 transition-colors">
              <h3 className="text-white font-bold text-lg">{s.title}</h3>
              <p className="mt-1.5 text-zinc-400 text-sm">{s.desc}</p>
            </div>
            {idx < flowSteps.length - 1 && (
              <ArrowRight className="hidden md:block w-6 h-6 text-[#BEFF38] mx-auto" />
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {flowCards.map((c) => (
          <div key={c.title} className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-5 flex flex-col hover:border-[#BEFF38]/40 transition-colors">
            <h3 className="text-white font-bold text-lg">{c.title}</h3>
            <p className="mt-2 text-zinc-400 text-sm leading-relaxed flex-1">{c.desc}</p>
            <Link to={c.to || '/my-plan'} className="mt-4 inline-flex w-fit items-center px-5 py-2.5 rounded-full bg-[#BEFF38] text-black font-black text-[11px] uppercase tracking-widest hover:bg-[#a8e62e] transition-colors">
              {c.cta}
            </Link>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const WorkoutsSupport = () => (
  <section className="bg-[#0b0b0c] pb-14 lg:pb-16" id="workouts">
    <div className="max-w-[1180px] mx-auto px-5 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-8 items-center">
        <div className="rounded-2xl overflow-hidden border border-zinc-800">
          <img src={workoutImage} alt="Person doing dumbbell workout" className="w-full h-[260px] sm:h-[340px] object-cover" loading="lazy" />
        </div>
        <div>
          <h2 className="text-white text-3xl lg:text-4xl font-black tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Workouts that support the plan</h2>
          <p className="mt-3 text-zinc-400 text-base leading-relaxed">
            FreeFitFuel workouts are no longer just standalone pages. They now sit around the plan engine, so your training has a reason, a direction, and a next step.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/my-plan" data-testid="workouts-go-to-plan-cta" className="px-5 py-3 rounded-full bg-[#BEFF38] text-black font-black text-xs uppercase tracking-widest hover:bg-[#a8e62e] transition-colors">Go to My Plan</Link>
            <Link to="/my-plan?tab=training&sub=library" data-testid="workouts-view-library-cta" className="px-5 py-3 rounded-full bg-transparent border border-zinc-700 text-white font-black text-xs uppercase tracking-widest hover:border-white transition-colors">View Workout Library</Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const LongevityStrength = () => (
  <section className="bg-[#0b0b0c] pb-14 lg:pb-16">
    <div className="max-w-[1180px] mx-auto px-5 lg:px-8">
      <h2 className="text-white text-2xl lg:text-3xl font-black tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Featured pathway</h2>
      <div className="mt-5 rounded-2xl border border-[#ff7a1a]/30 bg-gradient-to-br from-[#1a0f06] to-[#0b0b0c] p-6 lg:p-8" data-testid="longevity-section">
        <p className="text-[10px] font-mono uppercase tracking-[0.22em] font-black text-[#FF7A1A]">Over-40 pathway</p>
        <h3 className="mt-2 text-white text-2xl font-black uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Longevity &amp; Strength Training</h3>
        <p className="mt-2 text-zinc-300 text-sm sm:text-base leading-relaxed max-w-3xl">
          Built for over-40s. Ten levels of dumbbell-first strength paired with explosive training — jumps, throws, sprints and contrast pairs — because power output decays faster than strength after 40, and keeping it is the difference between strong and athletic. Four sessions a week, never more. Lifting is always central.
        </p>
        <ul className="mt-4 grid sm:grid-cols-2 gap-2 text-sm text-zinc-300">
          <li className="flex gap-2"><span className="text-[#FF7A1A]">•</span>Dumbbell-first (safer if training alone)</li>
          <li className="flex gap-2"><span className="text-[#FF7A1A]">•</span>Strength block paired with explosive block every session</li>
          <li className="flex gap-2"><span className="text-[#FF7A1A]">•</span>Plyometrics, med-ball throws, sprints, contrast pairs</li>
          <li className="flex gap-2"><span className="text-[#FF7A1A]">•</span>Max 4 days a week — recovery is the point</li>
        </ul>
        <Link to="/my-plan?tab=training&sub=mastery" data-testid="longevity-start-cta" className="mt-5 inline-flex items-center px-5 py-3 rounded-full bg-[#BEFF38] text-black font-black text-xs uppercase tracking-widest hover:bg-[#a8e62e] transition-colors">Start Phase I</Link>
      </div>
    </div>
  </section>
);

export const StartPlan = () => (
  <section className="bg-[#0b0b0c] pb-14 lg:pb-16">
    <div className="max-w-[1180px] mx-auto px-5 lg:px-8">
      <h2 className="text-white text-3xl lg:text-4xl font-black tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Start your plan</h2>
      <p className="mt-3 text-zinc-400 text-base leading-relaxed max-w-3xl">
        Set your target and let the system guide you day by day, week by week, with training, nutrition, hydration and recovery all pointing in the same direction.
      </p>
      <div className="mt-5 flex flex-wrap gap-3">
        <Link to="/my-plan?tab=profile&sub=plan" data-testid="startplan-build-plan-cta" className="px-5 py-3 rounded-full bg-[#BEFF38] text-black font-black text-xs uppercase tracking-widest hover:bg-[#a8e62e] transition-colors">Build My Plan</Link>
        <Link to="/my-plan" data-testid="startplan-start-today-cta" className="px-5 py-3 rounded-full bg-transparent border border-zinc-700 text-white font-black text-xs uppercase tracking-widest hover:border-white transition-colors">Start Today</Link>
      </div>
    </div>
  </section>
);

export const ImportantNote = () => (
  <section className="bg-[#0b0b0c] pb-12">
    <div className="max-w-[1180px] mx-auto px-5 lg:px-8">
      <h2 className="text-white text-2xl lg:text-3xl font-black tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Important Note</h2>
      <p className="mt-3 text-zinc-400 text-sm leading-relaxed max-w-4xl">
        The content on FreeFitFuel™ is for general fitness education only and isn’t a substitute for personalised medical advice. Consult a qualified professional before starting a new programme, especially if you have any health conditions or injuries. Stop any exercise that causes sharp pain, dizziness, chest pain, or unusual shortness of breath.
      </p>
      <div className="mt-6">
        <Link to="/privacy" data-testid="important-note-privacy-link" className="inline-flex items-center px-5 py-2.5 rounded-full border border-zinc-700 text-white font-black text-xs uppercase tracking-widest hover:border-[#BEFF38] hover:text-[#BEFF38] transition-colors">Privacy Statement</Link>
      </div>
    </div>
  </section>
);
